import { ImprvCoinsLedgerResponseModel } from '@models/imprvCoins';
import {
  GoalAccessGrantModel,
  OrderInvoiceModel,
  OrdersResponseModel,
  UserOrderModel
} from '@models/orders';
import * as Sentry from '@sentry/nextjs';
import { useCallback, useState } from 'react';
import { useImprvApi } from './useImprvApi';

export const useOrdersService = () => {
  const imprvApi = useImprvApi();
  const [isPendingGetOrders, setIsPendingGetOrders] = useState(false);
  const [isPendingGetAccountOrders, setIsPendingGetAccountOrders] = useState(false);
  const [isPendingCreateGoalAccessGrant, setIsPendingCreateGoalAccessGrant] = useState(false);

  const getOrders = useCallback(async (): Promise<OrdersResponseModel> => {
    try {
      setIsPendingGetOrders(true);
      return await imprvApi.getOrders();
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    } finally {
      setIsPendingGetOrders(false);
    }
  }, [imprvApi]);

  const getImprvCoinsLedger = useCallback(async (): Promise<ImprvCoinsLedgerResponseModel> => {
    try {
      const ledger = await imprvApi.getImprvCoinsLedger();
      // A render-time throw escapes this chain and blanks the page; there is no error boundary.
      if (!Array.isArray(ledger.entries) || !Array.isArray(ledger.buckets)) {
        throw new Error('Malformed imprv coins ledger response');
      }
      return ledger;
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  }, [imprvApi]);

  const getAccountOrders = useCallback(async (): Promise<UserOrderModel[]> => {
    try {
      setIsPendingGetAccountOrders(true);
      return await imprvApi.getAccountOrders();
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    } finally {
      setIsPendingGetAccountOrders(false);
    }
  }, [imprvApi]);

  const generateOrderInvoice = useCallback(
    async (orderId: string): Promise<OrderInvoiceModel> => {
      try {
        return await imprvApi.generateOrderInvoice(orderId);
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
    },
    [imprvApi]
  );

  const getOrderInvoice = useCallback(
    async (orderId: string): Promise<OrderInvoiceModel> => {
      try {
        return await imprvApi.getOrderInvoice(orderId);
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
    },
    [imprvApi]
  );

  const downloadOrderInvoice = useCallback(
    async (orderId: string): Promise<Blob> => {
      try {
        return await imprvApi.downloadOrderInvoicePdf(orderId);
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
    },
    [imprvApi]
  );

  const getGoalAccessGrants = useCallback(
    async (goalEntityId: string): Promise<GoalAccessGrantModel[]> => {
      try {
        return await imprvApi.getGoalAccessGrants(goalEntityId);
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
    },
    [imprvApi]
  );

  const createGoalAccessGrant = useCallback(
    async (goalEntityId: string, email: string): Promise<GoalAccessGrantModel> => {
      try {
        setIsPendingCreateGoalAccessGrant(true);
        return await imprvApi.createGoalAccessGrant(goalEntityId, email);
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      } finally {
        setIsPendingCreateGoalAccessGrant(false);
      }
    },
    [imprvApi]
  );

  return {
    getOrders,
    isPendingGetOrders,
    getImprvCoinsLedger,
    getAccountOrders,
    isPendingGetAccountOrders,
    generateOrderInvoice,
    getOrderInvoice,
    downloadOrderInvoice,
    getGoalAccessGrants,
    createGoalAccessGrant,
    isPendingCreateGoalAccessGrant
  };
};
